import { useMemo, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, Spinner } from '@shared/ui';
import {
  LEAD_STATUSES,
  useChangeLeadStatusMutation,
  useLeadsQuery,
  type Lead,
  type LeadStatus,
} from '@entities/crm';
import { BoardColumn } from './board-column';
import { LeadPanel } from './lead-panel';
import { NewLeadDialog } from './new-lead-dialog';
import { useDragState } from '../model/drag-state';

export function CrmBoard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useLeadsQuery();
  const changeStatus = useChangeLeadStatusMutation();
  const { ctx, onDragStart, onDragOverColumn, onDragEnd } = useDragState();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);

  const byStatus = useMemo(() => {
    const map = new Map<LeadStatus, Lead[]>();
    for (const s of LEAD_STATUSES) map.set(s, []);
    for (const lead of data ?? []) map.get(lead.status)?.push(lead);
    return map;
  }, [data]);

  const handleDrop = (status: LeadStatus, e: DragEvent<HTMLDivElement>) => {
    const id = e.dataTransfer.getData('text/plain') || ctx.draggingId;
    onDragEnd();
    if (!id) return;
    const lead = data?.find((l) => l.id === id);
    if (!lead || lead.status === status) return;
    changeStatus.mutate({ id, status });
  };

  const selectedLead = selectedId ? data?.find((l) => l.id === selectedId) ?? null : null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col gap-3">
        <p>{t('products.loadFail', { message: error?.message ?? 'unknown' })}</p>
        <Button variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <>
        <div className="flex justify-end pb-3">
          <Button onClick={() => setNewLeadOpen(true)}>{t('crm.newLead')}</Button>
        </div>
        <EmptyState
          title={t('crm.empty.title')}
          description={t('crm.empty.description')}
          action={<Button onClick={() => setNewLeadOpen(true)}>{t('crm.empty.addManual')}</Button>}
        />
        <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setNewLeadOpen(true)}>{t('crm.newLead')}</Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
        {LEAD_STATUSES.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            leads={byStatus.get(status) ?? []}
            hover={ctx.hoverColumn === status}
            draggingId={ctx.draggingId}
            onDragOver={onDragOverColumn}
            onDrop={handleDrop}
            onDragStartCard={onDragStart}
            onDragEndCard={onDragEnd}
            onSelect={(id) => setSelectedId(id)}
          />
        ))}
      </div>

      <LeadPanel
        lead={selectedLead}
        open={Boolean(selectedLead)}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} />
    </div>
  );
}
